import { Buffer } from 'buffer'

export class EncryptionService {
    private key: CryptoKey | null = null
    private readonly algorithm = {
        name: 'AES-GCM',
        length: 256,
    }

    async initialize(keyString: string): Promise<void> {
        // Convert the key string to a CryptoKey
        const keyData = new TextEncoder().encode(keyString)
        const hash = await crypto.subtle.digest('SHA-256', keyData)
        this.key = await crypto.subtle.importKey(
            'raw',
            hash,
            this.algorithm,
            false,
            ['encrypt', 'decrypt']
        )
    }

    async encrypt(data: any): Promise<string> {
        if (!this.key) {
            throw new Error('Encryption service not initialized')
        }

        const iv = crypto.getRandomValues(new Uint8Array(12))
        const encodedData = new TextEncoder().encode(JSON.stringify(data))

        const encryptedData = await crypto.subtle.encrypt(
            {
                name: this.algorithm.name,
                iv,
            },
            this.key,
            encodedData
        )

        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encryptedData.byteLength)
        combined.set(iv)
        combined.set(new Uint8Array(encryptedData), iv.length)

        // Convert to base64
        return Buffer.from(combined).toString('base64')
    }

    async decrypt(encryptedData: string): Promise<any> {
        if (!this.key) {
            throw new Error('Encryption service not initialized')
        }

        // Convert from base64
        const combined = Buffer.from(encryptedData, 'base64')
        const iv = combined.slice(0, 12)
        const data = combined.slice(12)

        const decryptedData = await crypto.subtle.decrypt(
            {
                name: this.algorithm.name,
                iv: new Uint8Array(iv),
            },
            this.key,
            new Uint8Array(data)
        )

        const decodedData = new TextDecoder().decode(decryptedData)
        return JSON.parse(decodedData)
    }
}

// Create a singleton instance
export const encryptionService = new EncryptionService() 